import { transformLegacyLonLat } from '@/utils/geo-coord'
import type { AmapLngLatPoint } from './types'

const I_MINUTE = 60
const I_HOURS = I_MINUTE * 60
const I_DAY = I_HOURS * 24

/** 安全转 number，非法值（含 NaN / Infinity）返回 null */
export const toValidNumber = (value: unknown): number | null => {
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * 校验经纬度是否合法：必须是有限数且在地球坐标范围内。
 * 对应 WGS84/GCJ02/BD09 的理论上限，实测数据偶发 Infinity 或超范围均可被拦截。
 */
export const isValidCoord = (lon: unknown, lat: unknown): boolean => {
  const nLon = Number(lon)
  const nLat = Number(lat)
  return (
    Number.isFinite(nLon) && nLon >= -180 && nLon <= 180 &&
    Number.isFinite(nLat) && nLat >= -90 && nLat <= 90
  )
}

/**
 * 安全解析经纬度为点位对象，非法值（NaN / Infinity / 超范围）返回 null。
 * 避免使用 [0, 0] 兜底——null island 会导致地图跳到几内亚湾。
 */
export const parseCoord = (lon: unknown, lat: unknown): AmapLngLatPoint | null => {
  if (!isValidCoord(lon, lat)) return null
  return { lon: Number(lon), lat: Number(lat) }
}

/**
 * 将任意坐标系转为 GCJ02（高德坐标系）。
 * coordType 枚举：1=WGS84, 2=GCJ02（默认）, 3=BD09。
 */
export const toGcjPoint = (
  lon: number,
  lat: number,
  coordType: number = 2
): AmapLngLatPoint => {
  if (coordType === 1) {
    const [gcjLon, gcjLat] = transformLegacyLonLat(lon, lat, 1, 3)
    return { lon: gcjLon, lat: gcjLat }
  }
  if (coordType === 3) {
    const [gcjLon, gcjLat] = transformLegacyLonLat(lon, lat, 5, 3)
    return { lon: gcjLon, lat: gcjLat }
  }
  return { lon, lat }
}

/**
 * 从地址对象提取 GCJ02 点位。
 * 兼容 lon/lng 两种经度字段，非法值返回 null。
 */
export const getPointFromAddress = (
  addressItem: any
): AmapLngLatPoint | null => {
  const gcjPoint = addressItem?.latLonGcj || {}
  const lon = toValidNumber(gcjPoint.lon ?? gcjPoint.lng)
  const lat = toValidNumber(gcjPoint.lat)
  if (lon === null || lat === null) {
    return null
  }
  return { lon, lat }
}

/** 将总秒数依次按 天 / 时 / 分 整除取余，得到 [天, 时, 分] 三个非负整数 */
const splitDurationSeconds = (inputValue: number): number[] => {
  const units = [I_DAY, I_HOURS, I_MINUTE]
  const dateParts: number[] = []
  let remainder = inputValue
  for (const unit of units) {
    if (remainder >= unit) {
      dateParts.push(Math.floor(remainder / unit))
      remainder %= unit
    } else {
      dateParts.push(0)
    }
  }
  return dateParts
}

/** 秒数格式化为中文时长文本：X天Y小时Z分钟（零值自动忽略） */
export const formatDurationSeconds = (value: number): string => {
  const textValues = ['天', '小时', '分钟']
  const dateParts = splitDurationSeconds(value)
  let text = ''
  dateParts.forEach((item, index) => {
    if (item > 0) {
      text += `${item}${textValues[index]}`
    }
  })
  return text
}
